import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useClassificationExecutionDetail,
  useQualityExecutionDetail,
} from '@/hooks/useAgentExecutions';
import { usePageTitle } from '@/hooks/usePageTitle';

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-muted-foreground text-[10px] font-semibold tracking-widest uppercase">
        {label}
      </div>
      {children}
    </div>
  );
}

function BooleanGrid({ record }: { record: Record<string, unknown> }) {
  const entries = Object.entries(record).filter(([, v]) => typeof v === 'boolean') as [
    string,
    boolean,
  ][];
  if (entries.length === 0) {
    return <p className="text-muted-foreground text-xs italic">empty</p>;
  }
  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-1 sm:grid-cols-2 lg:grid-cols-3">
      {entries.map(([key, value]) => (
        <div key={key} className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground truncate font-mono">{key}</span>
          <Badge
            variant="outline"
            className={
              value
                ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300'
                : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
            }
          >
            {value ? 'true' : 'false'}
          </Badge>
        </div>
      ))}
    </div>
  );
}

function DataTable({ record }: { record: Record<string, unknown> }) {
  const entries = Object.entries(record).filter(([, v]) => typeof v !== 'boolean');
  if (entries.length === 0) {
    return <p className="text-muted-foreground text-xs italic">empty</p>;
  }
  return (
    <table className="w-full table-fixed text-xs">
      <tbody>
        {entries.map(([key, value]) => (
          <tr key={key} className="border-b align-top last:border-b-0">
            <td className="text-muted-foreground w-1/4 py-1.5 pr-3 font-mono break-words">{key}</td>
            <td className="py-1.5 break-words">
              {value === null || value === undefined ? (
                <span className="text-muted-foreground italic">—</span>
              ) : typeof value === 'object' ? (
                <pre className="overflow-x-auto text-[10px]">{JSON.stringify(value, null, 2)}</pre>
              ) : (
                <span className="leading-relaxed whitespace-pre-wrap">{String(value)}</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2 text-xs">
      <span className="text-muted-foreground w-28 shrink-0">{label}</span>
      <span className="min-w-0 break-words">{value}</span>
    </div>
  );
}

export function AgentExecutionDetailPage({ kind }: { kind: 'quality' | 'classification' }) {
  const isQuality = kind === 'quality';
  usePageTitle(isQuality ? 'Quality Execution' : 'Classifier Execution');
  const navigate = useNavigate();
  const { callId, name } = useParams<{ callId: string; name: string }>();

  const qualityQuery = useQualityExecutionDetail(
    isQuality ? callId : undefined,
    isQuality ? name : undefined,
  );
  const classifyQuery = useClassificationExecutionDetail(
    !isQuality ? callId : undefined,
    !isQuality ? name : undefined,
  );
  const { data, isLoading, isError } = isQuality ? qualityQuery : classifyQuery;

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <p className="text-destructive text-sm">Execution not found.</p>
      </div>
    );
  }

  const config = isQuality
    ? (data as { quality_config: { name: string; model_name: string } | null }).quality_config
    : (data as { classifier_config: { name: string; model_name: string } | null })
        .classifier_config;
  const version = isQuality
    ? (data as { rubric_version: string }).rubric_version
    : (data as { taxonomy_version: string }).taxonomy_version;
  const when = isQuality
    ? (data as { assessed_at: string }).assessed_at
    : (data as { classified_at: string }).classified_at;
  const flags = isQuality
    ? (data as { flags: Record<string, unknown> }).flags
    : (data as { labels: Record<string, unknown> }).labels;
  const reasoning = isQuality ? (data as { reasoning: string | null }).reasoning : null;

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
        <Button variant="outline" size="sm" asChild>
          <Link to={`/calls/${data.call_id}`}>
            <ExternalLink className="mr-2 h-3.5 w-3.5" /> View call
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {isQuality ? 'Quality assessment' : 'Classification'} · {data.name}
          </CardTitle>
          <CardDescription>
            {config?.name ?? 'inline agent node'} · {version} · {new Date(when).toLocaleString()}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            <MetaRow label="Call" value={<span className="font-mono">{data.call_id}</span>} />
            <MetaRow label="Phone" value={data.call?.phone_number ?? '—'} />
            <MetaRow label="Channel" value={data.call?.call_transport ?? '—'} />
            <MetaRow
              label="Call started"
              value={data.call ? new Date(data.call.created_at).toLocaleString() : '—'}
            />
            <MetaRow label="Config" value={config?.name ?? 'inline agent node (no config)'} />
            <MetaRow label="Model" value={config?.model_name ?? '—'} />
            <MetaRow label="Version" value={version} />
            <MetaRow
              label="Confidence"
              value={
                data.confidence_score != null ? `${Math.round(data.confidence_score * 100)}%` : '—'
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-5 pt-6">
          <Section label={isQuality ? 'Flags' : 'Labels'}>
            <BooleanGrid record={flags} />
          </Section>
          <Section label="Data">
            <DataTable record={data.data} />
          </Section>
          {reasoning && (
            <Section label="Reasoning">
              <p className="text-xs leading-relaxed whitespace-pre-wrap">{reasoning}</p>
            </Section>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Model input</CardTitle>
          <CardDescription>
            The exact composed input this run was fed (system instruction + user input). Only
            recorded for runs made after this feature shipped.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.model_input ? (
            <pre className="bg-muted/40 max-h-[32rem] overflow-auto rounded-md border p-3 text-[11px] leading-relaxed whitespace-pre-wrap">
              {data.model_input}
            </pre>
          ) : (
            <p className="text-muted-foreground text-xs italic">
              Not recorded for this run (older execution, skipped run, or inline agent node).
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
